<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="isLoading"
      :options.sync="tableOptions"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-options': [10, 20, 50, -1],
        'items-per-page-text': 'Filas por página:'
      }"
      class="elevation-0"
      loading-text="Compilando auditoría de entregas..."
      no-data-text="No se han realizado entregas físicas en esta sucursal todavía."
    >
      <template v-slot:[`item.folio`]="{ item }">
        <a class="font-weight-bold primary--text text-decoration-underline cursor-pointer" @click.prevent="openDetailModal(item)">
          #{{ item.folio }}
        </a>
      </template>

      <template v-slot:[`item.client_name`]="{ item }">
        <div class="font-weight-medium text-slate-800">{{ item.client_name }}</div>
        <div class="text-caption grey--text">Ruta: {{ item.origin_store_name }}</div>
      </template>

      <template v-slot:[`item.progress_percentage`]="{ item }">
        <div class="d-flex align-center" style="min-width: 130px;">
          <v-progress-linear :value="item.progress_percentage"
            color="secondary" height="6" rounded class="mr-2">
          </v-progress-linear>
          <span class="text-caption font-weight-bold grey--text text--darken-2">
            {{ item.progress_percentage }}%
          </span>
        </div>
        <div class="text-caption text-left grey--text mt-1">
          Surtido: <strong>{{ item.total_items_delivered }}</strong>
          de {{ item.total_items_deferred }} pz.
        </div>
      </template>

      <template v-slot:[`item.purchase_date`]="{ item }">
        {{ formatDate(item.purchase_date) }}
      </template>

      <template v-slot:[`item.last_delivery_date`]="{ item }">
        <span class="font-weight-medium font-sm">{{ formatDate(item.last_delivery_date) }}</span>
      </template>

      <template v-slot:[`item.status_description`]="{ item }">
        <v-chip small dark :color="getOverallStatusColor(item.status_id)" class="font-weight-medium">
          {{ item.status_description }}
        </v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex align-center justify-end">
          <!-- Botón: Imprimir Vale de Resguardo -->
          <v-tooltip top color="grey darken-3">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                class="mr-3"
                v-bind="attrs"
                v-on="on"
                @click="openCollectionTicketPreview(item)"
              >
                <v-icon>mdi-printer</v-icon>
              </v-btn>
            </template>
            <span>Imprimir Vale de Resguardo</span>
          </v-tooltip>

          <!-- Botón: Ver detalle de Auditoría -->
          <v-tooltip top color="grey darken-3">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="goToDetail(item)"
              >
                <v-icon color="primary">mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>Ver detalle</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <delivery-order-details-modal
      v-model="showModal"
      :folio="selectedFolio"
      :store-id="currentStoreId"
    />
  </div>
</template>

<script>
import ClientDeliveryService from '@/services/ClientDeliveryService';
import { getOverallStatusColor } from '@/utils/Constants';
import DeliveryOrderDetailsModal from './DeliveryOrderDetailsModal.vue';

export default {
  name: 'HistoryDeliveriesTable',
  components: { DeliveryOrderDetailsModal },
  props: {
    storeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      items: [],
      isLoading: false,
      totalItems: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      showModal: false,
      selectedFolio: null,
      currentStoreId: null,
      headers: [
        {
          text: 'FOLIO VENTA', value: 'folio', align: 'start', sortable: true,
        },
        { text: 'CLIENTE', value: 'client_name', sortable: true },
        { text: 'PROGRESO LOGÍSTICO', value: 'progress_percentage', sortable: true },
        { text: 'FECHA COMPRA', value: 'purchase_date', sortable: true },
        { text: 'ÚLTIMA ENTREGA', value: 'last_delivery_date', sortable: true },
        { text: 'ESTATUS', value: 'status_description', sortable: false },
        {
          text: '', value: 'actions', sortable: false, align: 'end',
        },
      ],
    };
  },
  watch: {
    tableOptions: {
      handler(newVal) {
        const limit = newVal.itemsPerPage > 0 ? newVal.itemsPerPage : 1000;
        const offset = (newVal.page - 1) * limit;

        // Cuando cambies el combo de "5" a "10", esto le avisa al componente Padre
        this.$emit('pagination', { limit, offset });
      },
      deep: true,
    },
  },
  methods: {
    getOverallStatusColor,
    openDetailModal(item) {
      this.selectedFolio = item.folio;
      this.currentStoreId = this.$store.getters.currentStoreId;
      this.showModal = true;
    },
    openCollectionTicketPreview(item) {
      const routeData = this.$router.resolve({
        name: 'resguardo-vale-ticket',
        params: { folio: String(item.folio), mode: 'todas' },
      });

      const openedTab = window.open(routeData.href, '_blank');

      if (!openedTab) {
        this.$router.push({
          name: 'resguardo-vale-ticket',
          params: { folio: String(item.folio) },
        });
      }
    },
    async loadData(params) {
      this.tableOptions.itemsPerPage = params.limit;
      this.tableOptions.page = Math.floor(params.offset / params.limit) + 1;

      this.isLoading = true;
      try {
        const apiParams = {
          store_id: params.store_id,
          search_query: params.search,
          start_date: params.start_date,
          end_date: params.end_date,
          status_filter: params.status_id,
          limit: params.limit,
          offset: params.offset,
        };

        const response = await ClientDeliveryService.getDeliveryHistory(apiParams);

        if (response.success || response.status === 'success') {
          this.items = response.results || [];
          this.totalItems = response.total_records || this.items.length;
        }
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Error interno del servidor';
        this.$emit('error', `No se pudo obtener el historial. ${errorMsg}`);
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
    goToDetail(item) {
      this.$router.push({
        // Asegúrate de que este 'name' sea el mismo que diste de alta en tu router.js
        // para la pantalla DeliveryHistoryDetail.vue
        name: 'delivery-history-detail',
        params: { folio: item.folio },
      });
    },
  },
};
</script>
<style scoped>
.cursor-pointer { cursor: pointer; }
</style>
